import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// import { useNavigate } from 'react-router-dom';
import { Loading } from '../../../../components/Loading';
import { setBirthdaySet } from '../../../../domains/Auth/slice';
import { Container } from '../../../../GlobalComponents/Container';
import { ErrorMessage } from '../../../../GlobalComponents/ErrorMessage';
import { Flex } from '../../../../GlobalComponents/Flex';
import { QueryResult } from '../../../../GlobalComponents/QueryResult';
import { StoreCard } from '../../../../GlobalComponents/StoreCard';
import { Title } from '../../../../GlobalComponents/Title';
import { RootStates } from '../../../../reduxstore';
import { useAppDispatch, useAppSelector } from '../../../../reduxstore/hooks';
import { useSetStoreId } from '../../../../Store/hooks/useSetStoreId';
import {
	FavoriteStorePayload,
	useUpdateFavoritesInfoMutation
} from '../../../api';
import {
	StoreFieldsFragment,
	useGetFavoriteStoresQuery
} from '../../../graphql';
import { setProfile } from '../../../state/profileSlice';
import { AddFavoriteButton } from '../components/AddFavoriteButton';
import { setStoreSelection } from '../../../../domains/UI/store/slice';

interface IStore {
	store: number;
	priority: number;
}

type FavoriteStoreProps = {
	stores: Array<IStore>;
};

const FavoriteStore = ({ stores }: FavoriteStoreProps): JSX.Element => {
	const dispatch = useAppDispatch();
	const [showMessage, setShowMessage] = useState<string>('');

	const navigate = useNavigate();
	const [, setStoreId] = useSetStoreId();
	const userId = useAppSelector((state: RootStates) => state.authReducer.id);
	const storeSelection = useAppSelector(
		state => state.uiReducer.storeSelection
	);

	const storeId = stores.map(store => store.store);
	const { data, loading, error } = useGetFavoriteStoresQuery({
		fetchPolicy: 'network-only',
		variables: { storeId }
	});
	const favoriteStores = data?.favoriteStores as Array<StoreFieldsFragment>;

	const [
		updateFavoritesInfo,
		{ isLoading: addLoading, isError: addIsError, error: addError }
	] = useUpdateFavoritesInfoMutation();

	const handleSelect = (storeId: number) => {
		setStoreId(storeId);
		navigate('/');
	};

	const onValidate = useCallback(() => {
		const len = favoriteStores.length;
		if (len >= 3) {
			setShowMessage(
				'You can only have 3 favorite stores, please remove one to add another!'
			);
			return false;
		}
		setShowMessage('');
		return true;
	}, [setShowMessage, favoriteStores]);

	const onSelectedStore = useCallback(
		(selectedStoreId: number) => {
			if (onValidate()) {
				const favData = favoriteStores.map((favorite, idx: number) => {
					return {
						store: favorite.storeId,
						priority: idx + 1
					};
				});

				favData.push({
					store: selectedStoreId,
					priority: favoriteStores.length + 1
				});

				const inputData: FavoriteStorePayload = {
					id: userId as number,
					favorite_stores: favData
				};

				updateFavoritesInfo(inputData)
					.unwrap()
					.then((res: ProfileResponse) => {
						dispatch(setProfile(res.data.profile));
						dispatch(setBirthdaySet(res.data.birthday_set));
					});
			}
			window.scrollTo({ top: 0, behavior: 'smooth' });
		},
		[onValidate, favoriteStores, userId, updateFavoritesInfo, dispatch]
	);

	useEffect(() => {
		if (
			favoriteStores &&
			storeSelection.selectedStoreId &&
			storeSelection.source === 'FAVORITE_MODEL'
		)
			onSelectedStore(storeSelection.selectedStoreId);
	}, [onSelectedStore, storeSelection, favoriteStores]);

	const onFavoriteRemove = (id: number) => {
		window.scrollTo({ top: 0 });
		try {
			setShowMessage('');
			const favData = favoriteStores
				.filter(f => f.storeId !== Number(id))
				.map((favorite, idx: number) => {
					return {
						store: favorite.storeId,
						priority: idx + 1
					};
				});

			const inputData: FavoriteStorePayload = {
				id: userId as number,
				favorite_stores: favData
			};

			updateFavoritesInfo(inputData)
				.unwrap()
				.then((res: ProfileResponse) => {
					dispatch(setProfile(res.data.profile));
					dispatch(setBirthdaySet(res.data.birthday_set));
				});
		} catch (error) {}
	};

	// onSetPrimary to set the primary card and to display the star icon
	const onSetPrimary = (id: number) => {
		try {
			setShowMessage('');
			const favData = favoriteStores.map((favorite, idx: number) => {
				if (favorite.storeId === Number(id)) {
					return {
						store: favorite.storeId,
						priority: 1
					};
				} else {
					return {
						store: favorite.storeId,
						priority: idx + 2
					};
				}
			});

			const inputData: FavoriteStorePayload = {
				id: userId as number,
				favorite_stores: favData
			};

			updateFavoritesInfo(inputData)
				.unwrap()
				.then((res: ProfileResponse) => {
					dispatch(setProfile(res.data.profile));
					dispatch(setBirthdaySet(res.data.birthday_set));
				});
		} catch (error) {}
	};

	// input parameters is storeid and check if the priority is 1
	const onGetPriority = (storeId: number) => {
		let priority = 1;
		stores
			.filter(f => f.store === Number(storeId))
			.map(store => {
				priority = store.priority;
				return priority;
			});
		return priority;
	};

	return (
		<Container>
			<Title>Favorite Stores</Title>
			{addLoading && <Loading />}

			{addIsError && <ErrorMessage error={addError} />}
			{showMessage && <ErrorMessage message={showMessage} />}

			<QueryResult loading={loading} data={data} error={error}>
				<Flex $flexDirection="column" $rowGap="12px">
					{favoriteStores &&
						favoriteStores.length > 0 &&
						favoriteStores.map((store: StoreFieldsFragment) => (
							<StoreCard
								key={store.storeId}
								store={store}
								forSettingOLOStore={true}
								source={'FAVORITE_LIST'}
								isPrimary={
									onGetPriority(store.storeId) === 1
										? true
										: false
								}
								onRemove={() => onFavoriteRemove(store.storeId)}
								buttonAction={() => handleSelect(store.storeId)}
								buttonText="ORDER"
								setPrimaryStore={onSetPrimary}
							/>
						))}

					{favoriteStores && favoriteStores.length <= 3 ? (
						<AddFavoriteButton
							displayText={'Add a Favorite Store'}
							onAdd={() =>
								dispatch(
									setStoreSelection({
										isOpen: true,
										forSettingOLOStore: false,
										showFavorite: true,
										buttonText: 'FAVORITE',
										source: 'FAVORITE_MODEL'
									})
								)
							}
						/>
					) : null}
				</Flex>
			</QueryResult>
		</Container>
	);
};

export default FavoriteStore;
